// Snippets
// import '@snippets/product-gallery'
// import '@snippets/back-in-stock-form'

const productWrap = class extends HTMLElement {
  constructor() {
    super()

    this.productSelect = this.querySelector('[data-product-select]')
    this.productPriceEls = this.querySelectorAll('[data-product-price]')
    this.productComparePriceWrapEls = this.querySelectorAll('[data-product-compare-price-wrap]')
    this.productComparePriceEls = this.querySelectorAll('[data-product-compare-price]')
    this.sectionId = this.getAttribute('data-section-id')
    this.submitButton = this.querySelector('[data-product-submit]')
    this.submitButtonTextEls = this.querySelectorAll('[data-product-submit-text]')
    this.quantityWrapper = this.querySelector('[data-product-quantity-wrapper]')
    this.bisButton = this.querySelector('[data-oos-bis-button]')
    this.bisSelect = this.querySelector('[data-bis-variant]')
    this.hideOos = Boolean(this.hasAttribute('data-hide-oos'))
    this.stickyAtcWrapper = this.querySelector('[data-sticky-atc]')
    this.stickyAtcButton = this.querySelector('[data-sticky-atc]')
    this.footer = document.querySelector('footer')
    this.skuEl = this.querySelector('[data-variant-sku]')
    this.form = this.querySelector('form')

    this.selectedVariantData = null
    this.selectedVariantSellingPlan = null
    this.isSellingPlanSelected = false
    this.productData = null

  }

  connectedCallback () {
    this.initProductSection()
  }

  async initProductSection() {
    try {
      this.productData = await fetch(`/products/${this.dataset.productHandle}.js`)
        .then(res => res.json())

      this.associatedProductsInit()
      this.productFormInit()
      this.productOptionsInit()
      this.productGalleriesInit()
      this.stickyAtcInit()
      this.initAtc()
    } catch (error) {
      console.error(error)
    }
  }

  productFormInit() {
    const currentUrl = new URL(document.location)

    this.productSelect.addEventListener('change', () => {
      this.getCurrentVariantData()
      this.updateProductPrices()
      this.updateButtonState()
      this.updateBisSelector()
      this.updateSku()

      if (window.location.href.indexOf('/products/') >= 0) {
        currentUrl.searchParams.set('variant', this.selectedVariantData.id)
        window.history.replaceState(null, null, currentUrl.toString())
      }
    })

    this.getCurrentVariantData()
  }

  updateButtonState() {
    this.stickyAtcWrapper.classList.toggle('hidden', !this.selectedVariantData || !this.selectedVariantData.available)

    if (!this.submitButton || !this.selectedVariantData) { return }
    const buttonState = this.selectedVariantData.available ? 'add' : 'sold-out'

    this.submitButton.disabled = !this.selectedVariantData.available
    this.submitButtonTextEls.forEach(textEl => textEl.classList.toggle('hide', buttonState !== textEl.dataset.state))
    this.quantityWrapper && this.quantityWrapper.classList.toggle('hidden', !this.selectedVariantData.available)

    const showBackInStock = Boolean(!this.selectedVariantData.available && !this.hideOos)
    this.submitButton.classList.toggle('hidden', showBackInStock)
    this.bisButton.classList.toggle('hidden', !showBackInStock)
  }

  async associatedProductsInit() {
    const associatedProductToggles = this.querySelectorAll('[data-associated-product-toggle]')
    let associatedProductEls = document.querySelectorAll(`[data-section-id="${this.sectionId}"]`)

    if (!associatedProductToggles.length) { return }

    if (!this.hasAttribute('data-associated-product-section')) {
      const sectionPromises = [...associatedProductToggles]
        .filter(toggle => toggle.dataset.handle !== this.dataset.productHandle)
        .map(toggle => fetch(`${toggle.dataset.url}?sections=${this.sectionId}`))

      const sectionPromisesResults = await Promise.allSettled(sectionPromises)
      for (const result of sectionPromisesResults) {
        if (result.status === 'fulfilled') {
          const response = await result.value.json()
          const parser = new window.DOMParser()
          const responseDoc = parser.parseFromString(response[this.sectionId], 'text/html')
          const responseSection = responseDoc.querySelector('product-wrap')
          responseSection.setAttribute('data-associated-product-section', '')
          responseSection.classList.add('hide')
          this.insertAdjacentElement('afterend', responseSection)
        } else {
          console.error(result.value)
        }
      }

      associatedProductEls = document.querySelectorAll(`[data-section-id="${this.sectionId}"]`)
    }

    associatedProductToggles.forEach(toggle => {
      toggle.addEventListener('change', async () => {
        const matchingSection = [...associatedProductEls].find(productEl => productEl.dataset.productHandle === toggle.dataset.handle)
        if (!matchingSection) { return }

        associatedProductEls.forEach(productEl => {
          productEl.classList.toggle('hide', productEl.dataset.productHandle !== toggle.dataset.handle)
        })

        const matchingSectionButton = matchingSection.querySelector(`[data-associated-product-toggle][data-handle="${toggle.dataset.handle}"]`)
        if (matchingSectionButton) {
          matchingSectionButton.checked = true
          matchingSectionButton.focus()
        }

        if (window.location.href.indexOf('/products/') >= 0) {
          const historyUrl = `${toggle.dataset.url}${matchingSection?.selectedVariantData
            ? `?variant=${matchingSection.selectedVariantData.id}`
            : ''
            }`
          window.history.replaceState({}, toggle.dataset.title, historyUrl)
        }

        window.scrollTo(0, 0)
      })
    })
  }

  getCurrentVariantData() {
    const variantId = this.productSelect.value
    this.selectedVariantData = this.productData.variants.find(variant => variant.id === Number(variantId))
  }

  productOptionsInit() {
    const optionsParentEl = this.querySelector('[data-product-options]')
    const optionGroups = this.querySelectorAll('[data-product-option-group]')
    const optionRadios = this.querySelectorAll('[data-product-option][data-type="radio"]')
    const variantStockMessage = this.querySelector('[data-variant-stock-message]')
    const quantitySelectInput = this.querySelector('[data-quantity-input]')
    const bisEl = this.querySelector('bis-form')

    if (!optionsParentEl || !optionGroups.length) { return }

    const unavailableDisplay = optionsParentEl.getAttribute('data-unavailable-display')

    const getNextExistingVariant = (selectedOptions, optionIndex) => {
      const nextVariant = this.productData.variants.find(variant => variant[`option${optionIndex}`] === selectedOptions[`option${optionIndex}`])
      const nextVariantOptions = nextVariant.options.reduce((object, option, index) => {
        object[`option${index + 1}`] = option
        return object
      }, {})

      return {
        nextVariantOptions,
        nextVariant
      }
    }

    const getSelectedOptions = () => {
      const selectedOptions = [...optionGroups].reduce((object, group) => {
        let value = null
        if (group.dataset.type === 'select') {
          const select = group.querySelector('[data-product-option]')
          value = select.value
        } else {
          value = group.querySelector('[data-product-option]:checked').value
        }

        object[`option${group.dataset.optionIndex}`] = value
        return object
      }, {})

      return selectedOptions
    }

    const updateSelectedText = (group, input) => {
      optionGroups.forEach(optionGroup => {
        const selectedInput = optionGroup.querySelector('[data-product-option]:checked')
        const selectedTextEl = optionGroup.querySelector('[data-product-option-group-selected-text]')
        if (!selectedTextEl || !selectedInput) return

        const selectedText = selectedInput.dataset.selectedText

        selectedTextEl.textContent = selectedText
      })
    }

    const updateVariantStockMessage = (policy, quantity) => {
      const variantQuantity = parseInt(quantity, 10)

      if (!variantStockMessage) return
      variantStockMessage.classList.add('hide')

      if (policy === 'deny' && variantQuantity > 0 && variantQuantity < parseInt(variantStockMessage.dataset.lowStockBreakpoint, 10)) {
        variantStockMessage.classList.remove('hide')
      }
    }

    const updateQuanitySelect = (selectedVariant) => {
      if (!quantitySelectInput || !selectedVariant) { return }

      const selectedOption = this.productSelect.querySelector(`option[value="${selectedVariant.id}"]`)
      const { inventoryQuantity, inventoryPolicy } = selectedOption.dataset

      if (quantitySelectInput.value > inventoryQuantity && inventoryQuantity > 0) {
        quantitySelectInput.value = inventoryQuantity
      }

      const maxValue = inventoryPolicy === 'continue' ? 99 : inventoryQuantity

      quantitySelectInput.max = maxValue

      updateVariantStockMessage(inventoryPolicy, inventoryQuantity)
    }

    const updateOptionRadios = (selectedOptions) => {
      optionRadios.forEach((optionRadio, index) => {
        const inputOptions = { ...selectedOptions }
        const parentItemEl = optionRadio.closest('[data-product-option-item]')
        const optionIndex = Number(optionRadio.dataset.optionIndex)

        inputOptions[`option${optionIndex}`] = optionRadio.value

        const matchingVariant = getVariantByOptions(inputOptions)

        optionRadio.checked = optionRadio.value === selectedOptions[`option${optionIndex}`]

        parentItemEl.classList.toggle('unavailable', !matchingVariant)
        parentItemEl.classList.toggle('soldout', matchingVariant?.available === false)
        
        const srLabel = parentItemEl.querySelector('[data-swatch-ada-label]')
        if (matchingVariant && matchingVariant.available) {
          srLabel.innerHTML = srLabel.dataset.swatchAdaLabel
        } else if (matchingVariant && !matchingVariant.available) {
          srLabel.innerHTML = `${srLabel.dataset.swatchAdaLabel} - Sold Out`
        } else {
          srLabel.innerHTML = `${srLabel.dataset.swatchAdaLabel} - Unavailable`
        }
      })
    }

    const getVariantByOptions = (options) => {
      const matchingVariant = this.productData.variants.find(variant => {
        let isMatch = true

        for (const [key, value] of Object.entries(options)) {
          if (variant[key] !== value) {
            isMatch = false
            break
          }
        }

        return isMatch
      })

      return matchingVariant || null
    }

    const setAtcButtonUnavailable = () => {
      if (!this.submitButton) { return }
      const buttonState = 'unavailable'
      this.submitButton.disabled = true
      this.submitButtonTextEls.forEach(textEl => textEl.classList.toggle('hide', buttonState !== textEl.dataset.state))
    }

    optionGroups.forEach(optionGroup => {
      optionGroup.addEventListener('change', (event) => {
        handleOptionChange(optionGroup)
      })
    })

    const handleOptionChange = (group) => {
      let selectedOptions = getSelectedOptions()
      let selectedVariant = getVariantByOptions(selectedOptions)

      if (!selectedVariant && unavailableDisplay === 'select-next-available') {
        const nextVariantData = getNextExistingVariant(selectedOptions, group.dataset.optionIndex)
        selectedOptions = nextVariantData.nextVariantOptions
        selectedVariant = nextVariantData.nextVariant
      }

      updateOptionRadios(selectedOptions)
      updateSelectedText()
      updateQuanitySelect(selectedVariant)
      this.updateWishlist(selectedVariant)

      if (bisEl) {
        bisEl.handleVariantUpdate(selectedVariant)
      }

      if (selectedVariant) {
        this.productSelect.value = selectedVariant.id
        this.productSelect.dispatchEvent(new Event('change'))
      } else {
        setAtcButtonUnavailable()
      }
    }
    handleOptionChange(optionGroups[0])
  }

  updateProductPrices() {
    let price = this.selectedVariantData.price
    let comparePrice = this.selectedVariantData.compare_at_price

    // Base prices
    this.productPriceEls.forEach(productPriceEl => {
      productPriceEl.textContent = Shopify.formatMoney(price, theme.moneyFormat)
      productPriceEl.classList.toggle('product-price__price--sale', comparePrice && price < comparePrice)
    })

    // Compare at prices
    this.productComparePriceWrapEls.forEach(priceWrapEl => {
      priceWrapEl.classList.toggle('hide', (!comparePrice || price >= comparePrice))
    })

    this.productComparePriceEls.forEach(priceEl => {
      priceEl.textContent = Shopify.formatMoney(comparePrice, theme.moneyFormat)
    })
  }

  productGalleriesInit() {
    const galleryCarousels = this.querySelectorAll('product-gallery-carousel')
    const gridGallery = this.querySelector('product-gallery-grid')
    const simpleGallery = this.querySelector('product-gallery-simple')

    galleryCarousels.forEach(carousel => {
      carousel.selectedVariant = this.selectedVariantData
    })

    this.productSelect.addEventListener('change', () => {
      galleryCarousels.forEach(carousel => {
        carousel.selectedVariant = this.selectedVariantData
      })

      if (gridGallery) {
        gridGallery.selectedVariant = this.selectedVariantData
      }

      if (simpleGallery) {
        simpleGallery.selectedVariant = this.selectedVariantData
      }
    })
  }

  updateBisSelector () {
    if (!this.bisSelect || !this.selectedVariantData) { return }
    this.bisSelect.value = this.selectedVariantData.id
  }

  stickyAtcInit () {
    window.addEventListener('scroll', (e) => {
      this.checkStickyPos()
    })

    this.checkStickyPos()

    this.stickyAtcButton.addEventListener('click', () => {
      this.submitButton.click()
    })
  }

  checkStickyPos () {
    const atcRect = this.submitButton.getBoundingClientRect()
    const atcBottom = atcRect.bottom

    const footerRect = this.footer.getBoundingClientRect()
    const footerTop = footerRect.top
    const footerDiff = footerTop - (window.innerHeight * 0.5)

    this.stickyAtcWrapper.classList.toggle('active', (atcBottom <= 200) && footerDiff > 0)
  }

  updateSku () {
    if (!this.skuEl || !this.selectedVariantData) { return }
    this.skuEl.textContent = `${this.skuEl.dataset.text}${this.selectedVariantData.sku}`
  }

  updateWishlist (variant) {
    const wishlistButton = this.querySelector('[data-wishlist-button]')
    if (!variant || !wishlistButton) return

    wishlistButton.setAttribute('data-variant-id', variant.id)
    document.dispatchEvent(new CustomEvent('swym:variant-changed'));
  }

  initAtc () {
    this.submitButton.addEventListener('click', async (e) => {
      e.preventDefault()

      const formData = new FormData(this.form) // eslint-disable-line
      const atcData = Object.fromEntries(formData)

      if (typeof atcData.id === 'undefined' || !atcData.id) { return }

      const quantitySelectInput = this.querySelector('[data-quantity-input]')
      const propertiesObject = Object.keys(atcData)
        .filter(key => key.includes('properties'))
        .reduce((prev, cur, index) => {
          const name = cur.replace(/properties|\[|\]/g, '')
          if (name.length === 0 || !atcData[cur] || atcData[cur].length === 0) { return prev }
          prev[name] = atcData[cur]
          return prev
        }, {
          _inventory_quantity: quantitySelectInput.max || null
        })

      await cartAPI.cartAdd({
        items: [
          {
            id: atcData.id,
            quantity: atcData.quantity,
            properties: propertiesObject
          }
        ]
      })
      $.fancybox.close(true)
    })
  }
}

if (typeof window.customElements.get('product-wrap') === 'undefined') {
  window.customElements.define('product-wrap', productWrap)
}

const productShare = class extends HTMLElement {
  constructor() {
    super()

    this.button = this.querySelector('[data-share-button]')
    this.title = this.dataset.title
    this.text = this.dataset.text
  }

  connectedCallback() {
    this.button.addEventListener('click', () => {
      if (!navigator.share) { return }
      navigator.share({
        title: this.title,
        text: this.text,
        url: window.location.href
      }).then(() => {
        console.log('Page shared successfully')
      }).catch((error) => {
        console.error('Error sharing the page:', error)
      })
    })
  }
}

if (typeof window.customElements.get('product-share') === 'undefined') {
  window.customElements.define('product-share', productShare)
}

const copyLink = class extends HTMLElement {
  constructor() {
    super()

    this.button = this.querySelector('[data-copy-link-button]')
    this.input = this.querySelector('[data-link-to-copy]')
  }

  connectedCallback() {
    this.button.addEventListener('click', () => {
      this.input.select()
      this.input.setSelectionRange(0, 99999)
      navigator.clipboard.writeText(this.input.value)
      this.button.textContent = this.button.dataset.copiedText
    })
  }
}

if (typeof window.customElements.get('copy-link') === 'undefined') {
  window.customElements.define('copy-link', copyLink)
}

const BackInStock = class extends HTMLElement { // eslint-disable-line
  constructor() {
    super()

    this.apiKey = this.dataset.apiKey

    this.form = this.querySelector('[data-bis-form]')
    this.emailField = this.querySelector('input[name="email"]')
    this.variantSelect = this.querySelector('select[data-bis-variant]')
    this.fields = this.querySelectorAll('.field')

    this.success = this.querySelector('.success_message')
    this.error = this.querySelector('.error_message')
  }

  connectedCallback() {
    this.form && this.form.addEventListener('submit', (e) => {
      e.preventDefault()

      if (!this.emailField.checkValidity()) {
        this.error.classList.remove('hide')
        this.error.innerHTML = `<p>Please enter a valid email address.</p>`
        return
      }

      this.submitForm()
    })
  }

  async submitForm() {
    const formData = {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: this.emailField.value
              }
            }
          },
          channels: ["EMAIL"]
        },
        relationships: {
          variant: {
            data: {
              type: 'catalog-variant',
              id: `$shopify:::$default:::${this.variantSelect.value}`
            }
          }
        }
      }
    }

    const res = await fetch(`https://a.klaviyo.com/client/back-in-stock-subscriptions?company_id=${this.apiKey}`, {
      method: 'POST',
      headers: {
        revision: '2024-06-15',
        'content-type': 'application/json'
      },
      body: JSON.stringify(formData)
    })

    this.success && this.success.classList.toggle('hide', !res.ok)

    if (this.error) {
      this.error.classList.toggle('hide', res.ok)
      this.error.innerHTML = `<p>${res.statusText}</p>`
    }

    if (!res.ok) { return }
    const successEvent = new CustomEvent('submit_success', {
      detail: res
    })

    this.dispatchEvent(successEvent)
  }
}

if (typeof window.customElements.get('back-in-stock') === 'undefined') {
  window.customElements.define('back-in-stock', BackInStock)
}